/**
 * @module app.pp
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { I18N } from 'aurelia-i18n';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { apiCall } from 'app/shared/lib/essette-api';
import { APPROVED_DETERMINATION_TYPES, ICodes, NOT_APPLICABLE_CRITERIA_STATUS, EXTERNAL_SYSTEM_CODE, MAPPING_CODES, IOpenIQCParameters } from 'app/ext/iqc/iqc-manager';
import { AuthPortalPageContext } from 'app/pp/lib/auth-portal-page-context';
import { LookupService } from 'app/cd/services/lookup-service';
import { AuthExternalSystemErrorEvent, AuthIQCDocumentSkipEvent, AuthIQCGuidelinesSearchEvent, AuthIQCGuidelinesSearchTimeoutEvent, AuthIQCMedicalReviewEvent, IAuthChangeCustomEventDetail } from 'app/um/lib/events';
import { IqcManager } from 'app/ext/iqc/iqc-manager';
import { fireEvents } from 'app/shared/lib/fire-events';
import { IPopup } from 'app/shared/elements/popup-base';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class IqcDocumentStep extends ComponentBase implements IWizardStep {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected auth: UM.Auth | null = null;

    @bindable.boolean()
    protected readonly: boolean = false;

    @bindable.boolean()
    protected pastStep: boolean = false;

    // IWizard Step fields
    public canSkip: boolean = false;
    public canContinue: boolean = false;
    public canPerformFinalSubmit: boolean = false;

    protected isComplete: boolean = false;
    protected allHaveStatuses: boolean = false;
    protected isAllApproved: boolean = false;
    protected codes: ICodes[] = [];
    protected requiredCodes: ICodes[] = [];
    protected reviewedCodes: ICodes[] = [];
    protected ranMedicalReview: boolean = false;
    protected codeTitle: string | null = '';
    protected useDynamicSso: boolean = true;
    protected useRestrictedMode: boolean = true;
    protected iqcCommunicationsError: boolean = false;
    protected guidelinesFound: any[] = [];
    protected guidelinesSearched: boolean = false;
    protected externalFacilityId?: string | null = null;
    protected diagTypes: ReadonlyArray<CD.DiagnosisType> = [];
    protected serviceTypes: ReadonlyArray<CD.ServiceType> = [];
    private currentDiagnosesIndex = 0;
    private subscriptions: Array<Subscription> = [];

    ////****************** Element References ***************************************////
    protected $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly context: AuthPortalPageContext,
        protected iqc: IqcManager,
        protected readonly i18n: I18N,
        private readonly lookupService: LookupService,
        private readonly events: EventAggregator,
        private $element: Element,
        ...rest: any[]) {
        super(...rest);
    }

    protected attached(): void {
        if(!this.subscriptions.length) {
            this.subscriptions.push(this.events.subscribe(AuthIQCMedicalReviewEvent, () => this.handleMedicalReview()));
            this.subscriptions.push(this.events.subscribe(AuthExternalSystemErrorEvent, () => {
                // Handle event if guideline search fails, stop the loading state and allow user to skip
                if(this.state.loading) {
                    this.state.endLoad();
                }
                this.canSkip = this.iqcCommunicationsError = true;
            }));
            // Handles the IQC Guideline Search event
            this.subscriptions.push(this.events.subscribe(AuthIQCGuidelinesSearchEvent, (e: AuthIQCGuidelinesSearchEvent) => this.handleGuidelinesSearch(e)));
            this.subscriptions.push(this.events.subscribe(AuthIQCGuidelinesSearchTimeoutEvent, () => this.handleGuidelinesSearchTimeout()));
        }
    }

    protected bind(): void {
        this.configureExternalFacilityId();
        this.loadLookups();
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** Events ***************************************************////
    protected authChanged(): void {
        this.configureExternalFacilityId();
        this.loadCodes();
    }

    protected async handleMedicalReview(): Promise<void> {
        if(this.$element && this.auth) {
            await this.context.refreshAuthSettings();
            this.auth = this.context.auth;
            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: true }
            fireEvents(this.$element, ['auth-change'], detail);
            this.ranMedicalReview = true;
            this.authChanged();
            // Search next guidelines to document
            this.searchGuidelines();
        }
    }

    // Handle Callback from IQC Headless Guideline Search
    protected handleGuidelinesSearch(e: AuthIQCGuidelinesSearchEvent): void {
        if(e.guidelines) {
            this.guidelinesFound = e.guidelines;
        }
        if(!this.guidelinesSearched) {
            this.guidelinesSearched = true;
        }

        // If no guidelines were found
        if(!this.guidelinesFound.length) {
            //If reviewed codes exist, user can continue
            this.canContinue = this.reviewedCodes.length > 0;
            // If reviewed codes don't exist, can skip document medical necessity step
            this.canSkip = !this.canContinue;
        }
        this.state.endLoad();
    }

    // Handle Timeout from IQC Headless Guideline Search
    protected handleGuidelinesSearchTimeout(): void {
        // If guidelines are still searching but passed timeout, force stop search and raise time out modal error
        if(!this.guidelinesSearched) {
            this.guidelinesSearched = true;
            this.$modal.open();
        }
        this.canSkip = true;
        this.state.endLoad();
    }

    protected handleRetryGuidelineSearch(): void {
        this.$modal.close();
        this.searchGuidelines();
    }

    protected handleSkipGuidelineSearch(): void {
        this.$modal.close();
        this.events.publish(new AuthIQCDocumentSkipEvent());
    }

    protected configureExternalFacilityId(): void {
        this.externalFacilityId = this.auth?.provider?.requesting?.location?.facility?.identifier?.external;
    }

    ////****************** Functions ************************************************////
    public reset(): void {
        // Reset Medical Review Warning state
        this.ranMedicalReview = false;
        // Reset Initial Guideline Search fields
        this.iqcCommunicationsError = false;
        this.guidelinesFound = [];
        this.guidelinesSearched = false;
    }

    public skipStep(): void { }

    public continueStep(): void { }

    public openStep(): void {
        this.reset();

        if(this.codes.length < 1) {
            this.events.publish(new AuthIQCDocumentSkipEvent());
        }
        if(!this.externalFacilityId) {
            this.canSkip = true;
        }
        else {
            // If provider facility has an external facility identifier, initiate headless guideline search
            this.searchGuidelines();
        }
    }

    public async closeStep(): Promise<void> {
        await this.closeStepActions();
    }

    public async finalSubmit(): Promise<void> { }

    protected async loadLookups(): Promise<void> {
        await Promise.all([
            this.loadDiagnosisTypes(),
            this.loadServiceTypes()
        ]);
    }

    protected searchGuidelines(): void {
        this.guidelinesFound = [];
        if(!this.auth || !this.requiredCodes?.length) {
            this.guidelinesSearched = true;
            return;
        }

        this.state.beginLoad();
        this.guidelinesSearched = false;
        this.iqc.searchGuidelines(this.auth, this.requiredCodes, this.useDynamicSso);
    }

    protected openIqcPopup(): void {
        if(!this.auth) return;
        const services = this.auth?.services?.filter(s => s.bodySite?.bodySiteType?.code !== 'Dental');
        // Service Review
        const openIqcParameters: IOpenIQCParameters = {
            auth: this.auth,
            useDynamicSso: this.useDynamicSso,
            useRestrictedMode: this.useRestrictedMode,
            hideSaveReviewButton: true
        }
        if(services?.length) {
            openIqcParameters.services = this.auth.services;
            openIqcParameters.splitCodes = true;
            this.iqc.open(openIqcParameters);
        }
        // Diagnosis Review
        else if(this.auth.diagnoses?.length) {
            const allDiagnosesMet = this.auth.diagnoses.every(d => d.medicalReview?.criteriaStatus && d.medicalReview?.criteriaStatus !== NOT_APPLICABLE_CRITERIA_STATUS);
            let currentDiagnosisCriteriaStatus = this.auth.diagnoses[this.currentDiagnosesIndex].medicalReview?.criteriaStatus;
            // find the next diagnosis without an acceptable criteria status to send
            let nextDiagnosisFound = false;
            while(!allDiagnosesMet && !nextDiagnosisFound) {
                if(currentDiagnosisCriteriaStatus && currentDiagnosisCriteriaStatus !== NOT_APPLICABLE_CRITERIA_STATUS) {
                    this.currentDiagnosesIndex = (this.currentDiagnosesIndex + 1) % this.auth.diagnoses.length;
                    currentDiagnosisCriteriaStatus = this.auth.diagnoses[this.currentDiagnosesIndex].medicalReview?.criteriaStatus;
                } else {
                    nextDiagnosisFound = true;
                }
            }
            openIqcParameters.diagnosis = this.auth.diagnoses[this.currentDiagnosesIndex];
            this.iqc.open(openIqcParameters);
            // whether the user completes a medical review on the current diagnosis or not, move on to the next one
            this.currentDiagnosesIndex = (this.currentDiagnosesIndex + 1) % this.auth.diagnoses.length;
        }
        this.canSkip = !this.canContinue;
    }

    // Set a combined array of Diagnosis and Service codes
    protected loadCodes(): void {
        this.codes = [];
        this.requiredCodes = [];
        this.reviewedCodes = [];
        this.codeTitle = null;
        this.allHaveStatuses = false;
        this.isAllApproved = false;

        // Filter Out any Dental Services
        const services = this.auth?.services?.filter(s => s.definition?.type?.bodySiteType?.code !== 'Dental');

        // Services
        if(services?.length) {
            this.codeTitle = this.i18n.tr('pp:auths.iqcDocument.services.$title');
            services.forEach(s => {
                const isDetermination = s.status?.determination?.isDetermination;
                let determinationDescription = '';
                let isApprovedDetermination = false;

                if(isDetermination) {
                    determinationDescription = s.status?.determination?.status?.description ?? '';

                    const determinationType = s.status?.determination?.status?.determination?.result ?? '';

                    // Approved Determination if "approved" or "modified"
                    isApprovedDetermination = APPROVED_DETERMINATION_TYPES.indexOf(determinationType) > -1;
                }
                const code: ICodes = {
                    modelCode: "CD.Service",
                    code: s.definition?.code!,
                    description: s.definition?.description!,
                    type: this.serviceTypes.find(t => t.id == s.definition?.type!.id)?.mappings?.find(m => m.externalSystem!.code == EXTERNAL_SYSTEM_CODE)?.code!,
                    determination: determinationDescription,
                    isApprovedDetermination: isApprovedDetermination,
                    criteriaStatus: s.medicalReview?.criteriaStatus || '',
                    startDate: s.authorizedDate ?? null
                };

                // if criteria status is not applicable, do not display it
                if(code.criteriaStatus === NOT_APPLICABLE_CRITERIA_STATUS) code.criteriaStatus = '';
                this.codes.push(code);
            });
            // Confirm if all codes have a determination of approved status or applicable criteria status
            this.allHaveStatuses = !!this.codes.length && this.codes.every(c => c.isApprovedDetermination || c.criteriaStatus);
        }
        // Diagnoses
        else if(this.auth?.diagnoses?.length) {
            this.codeTitle = this.i18n.tr('pp:auths.iqcDocument.diagnoses.$title');
            this.auth?.diagnoses?.forEach(d => {
                const code: ICodes = {
                    modelCode: "CD.Diagnosis",
                    code: d.definition?.code!,
                    description: d.definition?.description!,
                    type: this.diagTypes.find(t => t.id == d.definition?.type!.id)?.mappings?.find(m => m.externalSystem!.code == EXTERNAL_SYSTEM_CODE)?.code!,
                    determination: '',
                    isApprovedDetermination: false,
                    criteriaStatus: d.medicalReview?.criteriaStatus || ''
                };

                // if criteria status is not applicable, do not display it
                if(code.criteriaStatus === NOT_APPLICABLE_CRITERIA_STATUS) code.criteriaStatus = '';
                this.codes.push(code);
            });
            // Confirm if all codes have an applicable criteria status
            this.allHaveStatuses = !!this.codes.length && this.codes.every(c => c.criteriaStatus);
        }

        // if all codes either have an applicable criteria status or a determination status, user can continue
        const unapprovedCodes = this.codes.filter(code => !code.isApprovedDetermination);
        this.requiredCodes = unapprovedCodes.filter(code => code.modelCode === "CD.Diagnosis" || Object.values(MAPPING_CODES).includes(code.type)).filter(code => (code.criteriaStatus === NOT_APPLICABLE_CRITERIA_STATUS || (code.criteriaStatus ? code.criteriaStatus.trim() : '') === ''))

        this.reviewedCodes = unapprovedCodes.filter(code => (code.criteriaStatus && code.criteriaStatus !== NOT_APPLICABLE_CRITERIA_STATUS && code.criteriaStatus.trim() !== ''));
        this.canContinue = this.requiredCodes.length == 0;

        // if user can continue, user can't skip
        if(this.canContinue) {
            this.canSkip = false;
            this.isAllApproved = this.codes.length > 0 && !unapprovedCodes.length;
        }
    }

    protected async closeStepActions(): Promise<void> {
        if(!this.context.portalIQC || !this.context.portalDeterminationRules
            || (this.auth?.diagnoses?.length == 0 && this.auth?.services?.length == 0)
        ) return;
        try {
            this.state.beginSave();

            await this.context.triggerDeterminationRules();
        } catch(error) {
            throw error
        } finally {
            this.state.endSave();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDiagnosisTypes(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.lookupService.loadDiagnosisTypes();
            this.diagTypes = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadServiceTypes(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.lookupService.loadServiceTypes();
            this.serviceTypes = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
